import { ClassValue } from 'clsx';
import { AnchoredView, AnchoredViewProps } from '@shared/components/AnchoredSheet';
import { View } from '@shared/components/View';
import { useFocusTrap } from '@shared/util/hooks';
import { useMenuContext } from './MenuContext';
import styles from './Menu.module.css';

/**
 * Menu.Content
 *
 * The floating dropdown container. Renders anchored to the nearest
 * `Menu.Trigger` or `Menu.Button` and traps focus while open. Returns null
 * when the menu is closed, so children are unmounted between opens.
 *
 * Pass `unpadded` to remove the default vertical padding (useful when the
 * content manages its own spacing). Pass `showScrollIndicators` to display
 * fade masks at the top and bottom when the list overflows.
 */

export interface MenuContentProps extends Omit<AnchoredViewProps, 'anchorName' | 'onClose'> {
  className?: ClassValue;
}

export function MenuContent({ children, className, ...rest }: MenuContentProps) {
  const { anchorName, isOpen, close, align, lightDismiss } = useMenuContext();
  const ref = useFocusTrap(isOpen);

  if (!isOpen) {
    return null;
  }

  return (
    <AnchoredView
      anchorName={anchorName}
      onClose={close}
      className={[
        styles.menu,
        align === 'left' && styles.alignLeft,
        align === 'center' && styles.alignCenter,
        className,
      ]}
      lightDismiss={lightDismiss}
      role="menu"
      {...rest}
    >
      <View ref={ref} className={[styles.menuContent]} data-content>
        {children}
      </View>
    </AnchoredView>
  );
}
